import React from "react";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Modal that appears when the Vimeo player fires the `ended` event.
 *
 * Post-2026-07-02 funnel restructure:
 *   - L.1..L.3: plain "Vai alla Lezione N+1" CTA (all free)
 *   - L.4: "Richiedi informazioni per sbloccare il resto del corso" (opens
 *     the info-request dialog — the gate to L.5+ paid content).
 *   - L.5..L.8: plain next-lesson nav (members-only, already unlocked).
 *   - L.9: back to /corso/area-membri.
 */
export default function LessonEndPopup({
  open, onOpenChange,
  n, user, upsellLoading, upsellError, onCtaClick,
}) {
  const isL4Gate = n === 4 && !user?.has_access_full_course;
  const alreadyRequested = !!user?.l2_info_requested_at;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] rounded-2xl border-0 p-0 overflow-hidden bg-white" data-testid={`end-popup-${n}`}>
        <div className="h-2 bg-tension w-full" />
        <div className="px-7 pt-7 pb-7 text-center">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-2xl md:text-[26px] leading-tight" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>
              Continua il percorso
            </DialogTitle>
            <DialogDescription className="text-[15px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
              {n >= 1 && n <= 3 && `Hai completato la Lezione ${n}. Passa alla Lezione ${n + 1}.`}
              {n === 4 && (isL4Gate
                ? (alreadyRequested
                    ? "✓ Hai richiesto informazioni per sbloccare il resto del corso — Alessandro ti contatterà al più presto."
                    : "Hai completato la Lezione 4. Richiedi informazioni per sbloccare il resto del corso.")
                : "Hai completato la Lezione 4. Passa alla Lezione 5.")}
              {n >= 5 && n < 9 && `Hai completato la Lezione ${n}. Passa alla Lezione ${n + 1}.`}
              {n === 9 && "Hai completato il protocollo. Hai concluso il corso Figlio a Carico."}
            </DialogDescription>
          </DialogHeader>

          {upsellError && (
            <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-left">
              {upsellError}
            </p>
          )}

          <button
            data-testid={`cta-next-${n}`}
            type="button"
            disabled={upsellLoading || (isL4Gate && alreadyRequested)}
            onClick={onCtaClick}
            className="cta-btn w-full mt-7 disabled:opacity-70"
          >
            {upsellLoading ? (
              <><Loader2 className="animate-spin" size={18} /> Sto elaborando…</>
            ) : (
              <>
                {n >= 1 && n <= 3 && <>Vai alla Lezione {n + 1} <ArrowRight size={18} strokeWidth={2.4} /></>}
                {n === 4 && (isL4Gate
                  ? (alreadyRequested
                      ? <><ShieldCheck size={18} /> ✓ Richiesta inviata, ti contatteremo al più presto</>
                      : <>Richiedi informazioni per sbloccare il resto del corso <ArrowRight size={18} strokeWidth={2.4} /></>)
                  : <>Vai alla Lezione 5 <ArrowRight size={18} strokeWidth={2.4} /></>)}
                {n >= 5 && n < 9 && <>Vai alla Lezione {n + 1} <ArrowRight size={18} strokeWidth={2.4} /></>}
                {n === 9 && <>Torna all&apos;area membri <ArrowRight size={18} strokeWidth={2.4} /></>}
              </>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
